<template>
  <div class="list-container">
    <h1 class="page-title">見積小分類マスタ一覧</h1>

    <div class="mb-3">
      <router-link
        v-if="isAdmin"
        :to="{ name: 'EstimateSubcategoryCreate', query: getSearchQuery() }"
        class="btn btn-sm btn-primary"
      >
        見積小分類を新規作成
      </router-link>
    </div>

    <div v-if="errorMessages.length" class="alert alert-danger">
      <div v-for="message in errorMessages" :key="message">{{ message }}</div>
    </div>

    <form class="search-container mb-3 bg-light" @submit.prevent="searchSubcategories">
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="estimateSubcategorySearchName" class="form-label">小分類名</label>
          <input
            id="estimateSubcategorySearchName"
            v-model.trim="search.name"
            type="text"
            class="form-control"
            placeholder="日本語名・英語名"
          >
        </div>

        <div class="col-md-4">
          <label for="estimateSubcategorySearchClassification" class="form-label">業務区分</label>
          <select
            id="estimateSubcategorySearchClassification"
            v-model="search.business_classification_id"
            class="form-select"
          >
            <option value="">すべて</option>
            <option
              v-for="classification in businessClassifications"
              :key="classification.id"
              :value="classification.id"
            >
              {{ classification.business_classification_nm }}
              {{ Number(classification.del_flg) === 1 ? '（削除済み）' : '' }}
            </option>
          </select>
        </div>

        <div class="col-md-3">
          <label for="estimateSubcategoryDeletedStatus" class="form-label">削除状態</label>
          <select
            id="estimateSubcategoryDeletedStatus"
            v-model="search.deleted_status"
            class="form-select"
          >
            <option value="active">削除されていないデータ</option>
            <option value="deleted">削除済みデータ</option>
            <option value="all">すべて</option>
          </select>
        </div>

        <div class="col-md-auto">
          <button type="submit" class="btn btn-sm btn-primary me-2">検索</button>
          <button type="button" class="btn btn-sm btn-secondary" @click="resetSearch">クリア</button>
        </div>
      </div>
    </form>

    <div class="d-flex justify-content-end">
      <Pagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-responsive">
      <table class="table table-bordered align-middle">
        <thead class="table-dark">
          <tr>
            <th>ID</th>
            <th>業務区分</th>
            <th>小分類名（日本語）</th>
            <th>小分類名（英語）</th>
            <th>状態</th>
            <th class="action-column"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="subcategory in subcategories"
            :key="subcategory.id"
            :class="{ 'table-secondary': Number(subcategory.del_flg) === 1 }"
          >
            <td>{{ subcategory.id }}</td>
            <td>{{ subcategory.business_classification.business_classification_nm }}</td>
            <td>{{ subcategory.subcategory_nm }}</td>
            <td>{{ subcategory.subcategory_nm_en || '-' }}</td>
            <td>{{ Number(subcategory.del_flg) === 0 ? '有効' : '削除済み' }}</td>
            <td class="text-nowrap">
              <router-link
                v-if="isAdmin"
                :to="{
                  name: 'EstimateSubcategoryEdit',
                  params: { id: subcategory.id },
                  query: getSearchQuery()
                }"
                class="btn btn-sm btn-success me-2"
              >
                編集
              </router-link>
              <button
                v-if="isAdmin && Number(subcategory.del_flg) === 0"
                type="button"
                class="btn btn-sm btn-danger"
                data-bs-toggle="modal"
                data-bs-target="#deleteModal"
                @click="paramsIdToDelete = subcategory.id"
              >
                削除
              </button>
              <button
                v-if="isAdmin && Number(subcategory.del_flg) === 1"
                type="button"
                class="btn btn-sm btn-warning"
                data-bs-toggle="modal"
                data-bs-target="#restoreModal"
                @click="paramsIdToRestore = subcategory.id"
              >
                復元
              </button>
            </td>
          </tr>
          <tr v-if="subcategories.length === 0">
            <td colspan="6" class="text-center text-muted">該当する見積小分類はありません</td>
          </tr>
        </tbody>
      </table>
    </div>

    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="deleteSubcategory"
    />
    <RestoreConfirmationModal
      :paramsId="paramsIdToRestore"
      @confirmRestore="restoreSubcategory"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  deleteEstimateSubcategory,
  getBusinessClassifications,
  getEstimateSubcategories,
  restoreEstimateSubcategory
} from '@/api/axios';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';
import RestoreConfirmationModal from '@/components/shared/RestoreConfirmationModal.vue';
import { extractApiErrors } from '@/utils/estimates';

document.title = '見積小分類マスタ一覧';

const store = useStore();
const route = useRoute();
const router = useRouter();
const isAdmin = computed(() => [0, 999].includes(Number(store.state.userRole)));
const subcategories = ref([]);
const businessClassifications = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const errorMessages = ref([]);
const paramsIdToDelete = ref(null);
const paramsIdToRestore = ref(null);
const search = reactive({
  name: '',
  business_classification_id: '',
  deleted_status: 'active'
});

const getSearchQuery = () => ({
  estimate_subcategory_name: search.name,
  estimate_subcategory_business_classification_id: search.business_classification_id,
  estimate_subcategory_deleted_status: search.deleted_status,
  estimate_subcategory_page: currentPage.value || 1
});

const fetchSubcategories = async (page = 1) => {
  errorMessages.value = [];
  try {
    const response = await getEstimateSubcategories({
      name: search.name || undefined,
      business_classification_id: search.business_classification_id || undefined,
      deleted_status: search.deleted_status,
      page
    });
    subcategories.value = response.data.estimate_subcategories || [];
    currentPage.value = response.data.meta?.current_page || page;
    totalPages.value = Math.max(response.data.meta?.total_pages || 1, 1);
  } catch (error) {
    subcategories.value = [];
    errorMessages.value = extractApiErrors(error);
  }
};

const loadBusinessClassifications = async () => {
  try {
    const response = await getBusinessClassifications({ deleted_status: 'all' });
    businessClassifications.value = response.data || [];
  } catch (error) {
    errorMessages.value = [
      ...errorMessages.value,
      ...extractApiErrors(error)
    ];
  }
};

const searchSubcategories = async () => {
  currentPage.value = 1;
  await fetchSubcategories(1);
  await router.push({
    name: 'EstimateSubcategoryList',
    query: getSearchQuery()
  });
};

const resetSearch = async () => {
  search.name = '';
  search.business_classification_id = '';
  search.deleted_status = 'active';
  currentPage.value = 1;
  await fetchSubcategories(1);
  await router.replace({
    name: 'EstimateSubcategoryList',
    query: getSearchQuery()
  });
};

const handleChangePage = async (page) => {
  if (page < 1 || page > totalPages.value) return;

  await fetchSubcategories(page);
  await router.replace({
    name: 'EstimateSubcategoryList',
    query: getSearchQuery()
  });
};

const deleteSubcategory = async (subcategoryId) => {
  try {
    await deleteEstimateSubcategory(subcategoryId);
    store.dispatch('setFlashMessage', {
      message: '見積小分類を削除しました',
      type: 'success'
    });
    await fetchSubcategories(currentPage.value);
  } catch (error) {
    errorMessages.value = extractApiErrors(error);
  }
};

const restoreSubcategory = async (subcategoryId) => {
  try {
    await restoreEstimateSubcategory(subcategoryId);
    store.dispatch('setFlashMessage', {
      message: '見積小分類を復元しました',
      type: 'success'
    });
    await fetchSubcategories(currentPage.value);
  } catch (error) {
    errorMessages.value = extractApiErrors(error);
  }
};

onMounted(async () => {
  const queryPage = Number(route.query.estimate_subcategory_page);
  search.name = route.query.estimate_subcategory_name || '';
  search.business_classification_id =
    route.query.estimate_subcategory_business_classification_id || '';
  search.deleted_status = route.query.estimate_subcategory_deleted_status || 'active';
  currentPage.value = Number.isInteger(queryPage) && queryPage > 0 ? queryPage : 1;

  await Promise.all([
    loadBusinessClassifications(),
    fetchSubcategories(currentPage.value)
  ]);
});
</script>

<style scoped>
.action-column {
  min-width: 135px;
}
</style>
